import { ScannerData } from '../types/ScannerData';
import { StorageManager } from '../utils/storageManager';

export interface AccumulatedDataRecord {
  id: string;
  symbol: string;
  underlying: string;
  strike: number;
  expiryDate: string;
  optionType: 'C' | 'P';
  timestamp: string;
  sessionId: string;
  systemRestartCount: number;
  
  // Current session data
  bidCount: number;
  askCount: number;
  tradeCount: number;
  bidVolume: number;
  askVolume: number;
  delta: number;
  direction: string;
  sentiment: string;
  
  // Accumulated data (persisted across sessions)
  accumulatedBidCount: number;
  accumulatedAskCount: number;
  accumulatedTrades: number;
  accumulatedBidVolume: number;
  accumulatedAskVolume: number;
  accumulatedDelta: number;
  accumulatedDirection: string;
  accumulatedSentiment: string;
  
  // Metadata
  lastUpdated: string;
  dataSource: string; // 'tradestation', 'manual', 'calculated'
  confidence: number; // 0-100, how confident we are in the accumulated data
  notes: string;
}

export interface DataResetEvent {
  id: string;
  timestamp: string;
  sessionId: string;
  resetType: 'TRADESTATION_RESET' | 'MANUAL_RESET' | 'SYSTEM_RESTART';
  affectedSymbols: string[];
  previousAccumulatedData: { [symbol: string]: Partial<AccumulatedDataRecord> };
  recoveryStatus: 'PENDING' | 'RECOVERED' | 'FAILED';
  recoveryNotes: string;
}

export class AccumulatedDataPersistence {
  private dataKey = 'accumulated_data_records';
  private resetEventsKey = 'data_reset_events';
  private sessionId: string;
  private systemRestartCount: number;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.systemRestartCount = this.getSystemRestartCount();
  }

  private generateSessionId(): string {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const random = Math.random().toString(36).substring(2, 8);
    return `session_${timestamp}_${random}`;
  }

  private getSystemRestartCount(): number {
    try {
      const stored = localStorage.getItem('system_restart_count');
      const count = stored ? parseInt(stored, 10) : 0;
      const newCount = count + 1;
      localStorage.setItem('system_restart_count', newCount.toString());
      return newCount;
    } catch {
      return 1;
    }
  }

  // Process new data and merge with accumulated data
  processData(data: ScannerData[]): ScannerData[] {
    const accumulatedRecords = this.loadAccumulatedRecords();
    const processedData: ScannerData[] = [];
    
    for (const item of data) {
      const recordId = this.generateRecordId(item);
      const existingRecord = accumulatedRecords.find(r => r.id === recordId);
      
      if (existingRecord) {
        // Merge with existing accumulated data
          const mergedItem = this.mergeWithAccumulatedData(item, existingRecord);
          processedData.push(mergedItem);
          
          // Update the accumulated record
          this.updateAccumulatedRecord(existingRecord, item);
      } else {
        // Check if we have accumulated data for the same symbol (different strike/expiry)
        const symbolRecord = this.findSymbolAccumulatedData(item.Symbol);
        
        if (symbolRecord) {
            // Use accumulated data from same symbol as base
            const mergedItem = this.mergeWithSymbolAccumulatedData(item, symbolRecord);
            processedData.push(mergedItem);
            
            // Create new record with accumulated base
            const newRecord = this.createAccumulatedRecordFromSymbol(item, symbolRecord);
            accumulatedRecords.push(newRecord);
        } else {
          // Create new accumulated record
          const newRecord = this.createAccumulatedRecord(item);
          accumulatedRecords.push(newRecord);
          
          // Use current data as-is for first time
          processedData.push(item);
        }
      }
    }
    
    // Save updated accumulated records
    this.saveAccumulatedRecords(accumulatedRecords);
    
    return processedData;
  }

  // Detect data reset (when current data is much lower than accumulated)
  detectDataReset(data: ScannerData[]): DataResetEvent | null {
    const accumulatedRecords = this.loadAccumulatedRecords();
    const resetSymbols: string[] = [];
    const previousData: { [symbol: string]: Partial<AccumulatedDataRecord> } = {};
    
    for (const item of data) {
      const recordId = this.generateRecordId(item);
      const existingRecord = accumulatedRecords.find(r => r.id === recordId);
      
      if (existingRecord) {
        // Check if current data is significantly lower than accumulated
        const currentBidCount = item['Bid Count'] || 0;
        const currentAskCount = item['Ask Count'] || 0;
        const currentBidVolume = item['Bid Volume'] || 0;
        const currentAskVolume = item['Ask Volume'] || 0;
        
        const accumulatedBidCount = existingRecord.accumulatedBidCount || 0;
        const accumulatedAskCount = existingRecord.accumulatedAskCount || 0;
        const accumulatedBidVolume = existingRecord.accumulatedBidVolume || 0;
        const accumulatedAskVolume = existingRecord.accumulatedAskVolume || 0;
        
        // If current data is less than 10% of accumulated, consider it a reset
        const resetThreshold = 0.1;
        const isReset = (
          (currentBidCount < accumulatedBidCount * resetThreshold) ||
          (currentAskCount < accumulatedAskCount * resetThreshold) ||
          (currentBidVolume < accumulatedBidVolume * resetThreshold) ||
          (currentAskVolume < accumulatedAskVolume * resetThreshold)
        );
        
        if (isReset) {
          resetSymbols.push(item.Symbol);
          previousData[item.Symbol] = {
            accumulatedBidCount: existingRecord.accumulatedBidCount,
            accumulatedAskCount: existingRecord.accumulatedAskCount,
            accumulatedTrades: existingRecord.accumulatedTrades,
            accumulatedBidVolume: existingRecord.accumulatedBidVolume,
            accumulatedAskVolume: existingRecord.accumulatedAskVolume,
            accumulatedDelta: existingRecord.accumulatedDelta,
            accumulatedDirection: existingRecord.accumulatedDirection,
            accumulatedSentiment: existingRecord.accumulatedSentiment
          };
        }
      } else {
        // Check if this is a new strike/expiry for an existing symbol
        const symbolRecord = this.findSymbolAccumulatedData(item.Symbol);
        if (symbolRecord) {
          // Check if current data is zero/flat for new strike/expiry
          const currentBidCount = item['Bid Count'] || 0;
          const currentAskCount = item['Ask Count'] || 0;
          const currentBidVolume = item['Bid Volume'] || 0;
          const currentAskVolume = item['Ask Volume'] || 0;
          
          // If new strike/expiry has zero data, inherit accumulated from symbol
          if (currentBidCount === 0 && currentAskCount === 0 && currentBidVolume === 0 && currentAskVolume === 0) {
            resetSymbols.push(item.Symbol);
            previousData[item.Symbol] = {
              accumulatedBidCount: symbolRecord.accumulatedBidCount,
              accumulatedAskCount: symbolRecord.accumulatedAskCount,
              accumulatedTrades: symbolRecord.accumulatedTrades,
              accumulatedBidVolume: symbolRecord.accumulatedBidVolume,
              accumulatedAskVolume: symbolRecord.accumulatedAskVolume,
              accumulatedDelta: symbolRecord.accumulatedDelta,
              accumulatedDirection: symbolRecord.accumulatedDirection,
              accumulatedSentiment: symbolRecord.accumulatedSentiment
            };
          }
        }
      }
    }
    
    if (resetSymbols.length > 0) {
      const resetEvent: DataResetEvent = {
        id: `reset_${Date.now()}`,
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
        resetType: 'TRADESTATION_RESET',
        affectedSymbols: resetSymbols,
        previousAccumulatedData: previousData,
        recoveryStatus: 'PENDING',
        recoveryNotes: `Detected data reset for ${resetSymbols.length} symbols. Current data is significantly lower than accumulated values or new strikes have zero data.`
      };
      
      this.saveResetEvent(resetEvent);
      return resetEvent;
    }
    
    return null;
  }

  // Recover accumulated data after reset
  recoverAccumulatedData(resetEvent: DataResetEvent): boolean {
    try {
      const accumulatedRecords = this.loadAccumulatedRecords();
      let recoveredCount = 0;
      
      for (const symbol of resetEvent.affectedSymbols) {
        const previousData = resetEvent.previousAccumulatedData[symbol];
        if (previousData) {
          // Find and update the accumulated record
          const recordIndex = accumulatedRecords.findIndex(r => r.symbol === symbol);
          if (recordIndex !== -1) {
            const record = accumulatedRecords[recordIndex];
            
            // Restore accumulated values
            record.accumulatedBidCount = previousData.accumulatedBidCount || record.accumulatedBidCount;
            record.accumulatedAskCount = previousData.accumulatedAskCount || record.accumulatedAskCount;
            record.accumulatedTrades = previousData.accumulatedTrades || record.accumulatedTrades;
            record.accumulatedBidVolume = previousData.accumulatedBidVolume || record.accumulatedBidVolume;
            record.accumulatedAskVolume = previousData.accumulatedAskVolume || record.accumulatedAskVolume;
            record.accumulatedDelta = previousData.accumulatedDelta || record.accumulatedDelta;
            record.accumulatedDirection = previousData.accumulatedDirection || record.accumulatedDirection;
            record.accumulatedSentiment = previousData.accumulatedSentiment || record.accumulatedSentiment;
            
            record.lastUpdated = new Date().toISOString();
            record.confidence = Math.min(record.confidence + 10, 100); // Increase confidence after recovery
            
            accumulatedRecords[recordIndex] = record;
            recoveredCount++;
          }
        }
      }
      
      // Update reset event status
      resetEvent.recoveryStatus = recoveredCount > 0 ? 'RECOVERED' : 'FAILED';
      resetEvent.recoveryNotes = `Recovered accumulated data for ${recoveredCount} out of ${resetEvent.affectedSymbols.length} symbols.`;
      this.updateResetEvent(resetEvent);
      
      // Save updated records
      this.saveAccumulatedRecords(accumulatedRecords);
      
      return recoveredCount > 0;
    } catch (error) {
      console.error('Error recovering accumulated data:', error);
      resetEvent.recoveryStatus = 'FAILED';
      resetEvent.recoveryNotes = `Recovery failed: ${error}`;
      this.updateResetEvent(resetEvent);
      return false;
    }
  }

  // Get accumulated data for a specific symbol
  getAccumulatedData(symbol: string, strike?: number, expiryDate?: string): AccumulatedDataRecord | null {
    const accumulatedRecords = this.loadAccumulatedRecords();
    const recordId = this.generateRecordIdFromParams(symbol, strike, expiryDate);
    return accumulatedRecords.find(r => r.id === recordId) || null;
  }

  // Find accumulated data for the same symbol (any strike/expiry)
  findSymbolAccumulatedData(symbol: string): AccumulatedDataRecord | null {
    const accumulatedRecords = this.loadAccumulatedRecords();
    return accumulatedRecords.find(r => r.symbol === symbol) || null;
  }

  // Get all accumulated data
  getAllAccumulatedData(): AccumulatedDataRecord[] {
    return this.loadAccumulatedRecords();
  }

  // Internal sync - no export/import functionality
  // Data is automatically managed and synced locally only

  // Private helper methods
  private generateRecordId(item: ScannerData): string {
    const symbol = item.Symbol || '';
    const strike = item.Strike || 0;
    const expiry = item['Expiry Date'] || '';
    const optionType = item['Option Type'] || '';
    
    return `${symbol}_${strike}_${expiry}_${optionType}`.replace(/[^a-zA-Z0-9_-]/g, '_');
  }

  private generateRecordIdFromParams(symbol: string, strike?: number, expiryDate?: string): string {
    const strikeStr = strike || 0;
    const expiryStr = expiryDate || '';
    return `${symbol}_${strikeStr}_${expiryStr}`.replace(/[^a-zA-Z0-9_-]/g, '_');
  }

  private mergeWithAccumulatedData(item: ScannerData, record: AccumulatedDataRecord): ScannerData {
    const merged = { ...item };
    
    // Add accumulated fields to the item
    merged['Bid Count accumelated'] = record.accumulatedBidCount;
    merged['Ask Count accumelated'] = record.accumulatedAskCount;
    merged['Trades accumelated'] = record.accumulatedTrades;
    merged['Bid Volume accumelated'] = record.accumulatedBidVolume;
    merged['Ask Volume accumelated'] = record.accumulatedAskVolume;
    merged['Delta accumelated'] = record.accumulatedDelta;
    merged['Direction accumelated'] = record.accumulatedDirection;
    merged['Sentiment accumelated'] = record.accumulatedSentiment;
    
    return merged;
  }

  private mergeWithSymbolAccumulatedData(item: ScannerData, symbolRecord: AccumulatedDataRecord): ScannerData {
    const merged = { ...item };
    
    // Use accumulated data from same symbol as base
    merged['Bid Count accumelated'] = symbolRecord.accumulatedBidCount;
    merged['Ask Count accumelated'] = symbolRecord.accumulatedAskCount;
    merged['Trades accumelated'] = symbolRecord.accumulatedTrades;
    merged['Bid Volume accumelated'] = symbolRecord.accumulatedBidVolume;
    merged['Ask Volume accumelated'] = symbolRecord.accumulatedAskVolume;
    merged['Delta accumelated'] = symbolRecord.accumulatedDelta;
    merged['Direction accumelated'] = symbolRecord.accumulatedDirection;
    merged['Sentiment accumelated'] = symbolRecord.accumulatedSentiment;
    
    return merged;
  }

  private createAccumulatedRecord(item: ScannerData): AccumulatedDataRecord {
    return {
      id: this.generateRecordId(item),
      symbol: item.Symbol || '',
      underlying: item.Underlying || '',
      strike: item.Strike || 0,
      expiryDate: item['Expiry Date'] || '',
      optionType: item['Option Type'] || 'C',
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      systemRestartCount: this.systemRestartCount,
      
      // Current session data
      bidCount: item['Bid Count'] || 0,
      askCount: item['Ask Count'] || 0,
      tradeCount: item['Trade Count'] || 0,
      bidVolume: item['Bid Volume'] || 0,
      askVolume: item['Ask Volume'] || 0,
      delta: item['Delta ( ask - bid )'] || 0,
      direction: item.Direction || '',
      sentiment: item.Sentiment || '',
      
      // Accumulated data (start with current values)
      accumulatedBidCount: item['Bid Count'] || 0,
      accumulatedAskCount: item['Ask Count'] || 0,
      accumulatedTrades: item['Trade Count'] || 0,
      accumulatedBidVolume: item['Bid Volume'] || 0,
      accumulatedAskVolume: item['Ask Volume'] || 0,
      accumulatedDelta: item['Delta ( ask - bid )'] || 0,
      accumulatedDirection: item.Direction || '',
      accumulatedSentiment: item.Sentiment || '',
      
      // Metadata
      lastUpdated: new Date().toISOString(),
      dataSource: 'tradestation',
      confidence: 50, // Initial confidence
      notes: 'Initial record created'
    };
  }

  private createAccumulatedRecordFromSymbol(item: ScannerData, symbolRecord: AccumulatedDataRecord): AccumulatedDataRecord {
    return {
      id: this.generateRecordId(item),
      symbol: item.Symbol || '',
      underlying: item.Underlying || '',
      strike: item.Strike || 0,
      expiryDate: item['Expiry Date'] || '',
      optionType: item['Option Type'] || 'C',
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      systemRestartCount: this.systemRestartCount,
      
      // Current session data
      bidCount: item['Bid Count'] || 0,
      askCount: item['Ask Count'] || 0,
      tradeCount: item['Trade Count'] || 0,
      bidVolume: item['Bid Volume'] || 0,
      askVolume: item['Ask Volume'] || 0,
      delta: item['Delta ( ask - bid )'] || 0,
      direction: item.Direction || '',
      sentiment: item.Sentiment || '',
      
      // Accumulated data (inherit from symbol record)
      accumulatedBidCount: symbolRecord.accumulatedBidCount,
      accumulatedAskCount: symbolRecord.accumulatedAskCount,
      accumulatedTrades: symbolRecord.accumulatedTrades,
      accumulatedBidVolume: symbolRecord.accumulatedBidVolume,
      accumulatedAskVolume: symbolRecord.accumulatedAskVolume,
      accumulatedDelta: symbolRecord.accumulatedDelta,
      accumulatedDirection: symbolRecord.accumulatedDirection,
      accumulatedSentiment: symbolRecord.accumulatedSentiment,
      
      // Metadata
      lastUpdated: new Date().toISOString(),
      dataSource: 'tradestation',
      confidence: Math.min(symbolRecord.confidence + 10, 100), // Inherit confidence
      notes: `Created from symbol ${symbolRecord.symbol} accumulated data`
    };
  }

  private updateAccumulatedRecord(record: AccumulatedDataRecord, item: ScannerData): void {
    // Add current values to accumulated totals
    record.accumulatedBidCount += item['Bid Count'] || 0;
    record.accumulatedAskCount += item['Ask Count'] || 0;
    record.accumulatedTrades += item['Trade Count'] || 0;
    record.accumulatedBidVolume += item['Bid Volume'] || 0;
    record.accumulatedAskVolume += item['Ask Volume'] || 0;
    
    // Update current session data
    record.bidCount = item['Bid Count'] || 0;
    record.askCount = item['Ask Count'] || 0;
    record.tradeCount = item['Trade Count'] || 0;
    record.bidVolume = item['Bid Volume'] || 0;
    record.askVolume = item['Ask Volume'] || 0;
    record.delta = item['Delta ( ask - bid )'] || 0;
    record.direction = item.Direction || record.direction;
    record.sentiment = item.Sentiment || record.sentiment;
    
    // Update accumulated delta (weighted average)
    const totalVolume = record.accumulatedBidVolume + record.accumulatedAskVolume;
    if (totalVolume > 0) {
      const currentWeight = ((item['Bid Volume'] || 0) + (item['Ask Volume'] || 0)) / totalVolume;
      const previousWeight = 1 - currentWeight;
      record.accumulatedDelta = (record.accumulatedDelta * previousWeight) + ((item['Delta ( ask - bid )'] || 0) * currentWeight);
    }
    
    record.lastUpdated = new Date().toISOString();
    record.confidence = Math.min(record.confidence + 1, 100); // Gradually increase confidence
  }

  private loadAccumulatedRecords(): AccumulatedDataRecord[] {
    return StorageManager.getItem<AccumulatedDataRecord[]>(this.dataKey, []);
  }

  private saveAccumulatedRecords(records: AccumulatedDataRecord[]): void {
    StorageManager.setItem(this.dataKey, records, 'low');
  }

  private loadResetEvents(): DataResetEvent[] {
    return StorageManager.getItem<DataResetEvent[]>(this.resetEventsKey, []);
  }

  private saveResetEvent(event: DataResetEvent): void {
    const events = this.loadResetEvents();
    events.push(event);
    StorageManager.setItem(this.resetEventsKey, events, 'low');
  }

  private updateResetEvent(event: DataResetEvent): void {
    const events = this.loadResetEvents();
    const index = events.findIndex(e => e.id === event.id);
    if (index !== -1) {
      events[index] = event;
      StorageManager.setItem(this.resetEventsKey, events, 'low');
    }
  }

  private saveResetEvents(events: DataResetEvent[]): void {
    StorageManager.setItem(this.resetEventsKey, events, 'low');
  }
} 